import { ExternalReturnHubType } from "@redotech/account-common/address";
import type { TeamLocation } from "@redotech/admin-sdk/rpc/schema/locations/get-team-locations";
import { useRequiredContext } from "@redotech/react-util/context";
import { useRpcData } from "@redotech/react-util/rpc-data";
import Edit01Icon from "@redotech/redo-icons/edit-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import type { InputSchema } from "@redotech/ui/form";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import type {
  availableReturnsCentersInput,
  ExternalLocationHubFormConfig,
  ReturnsCenterConfigFormValue,
} from "./redo-managed-returns-settings-form";

type AvailableReturnsCentersForm = InputSchema.Input<
  typeof availableReturnsCentersInput
>;

interface ReturnsCenterRowData {
  id: string;
  label: string;
  schedule: string;
  addressSummary: string;
  provider: string;
  locationId: string;
  ownerTeamName: string;
  refulfillment: boolean;
}

const HUB_DISPLAY_NAMES: Record<ExternalReturnHubType, string> = {
  [ExternalReturnHubType.RETURNBEAR]: "ReturnBear",
  [ExternalReturnHubType.BLUEYONDER]: "Blue Yonder",
  [ExternalReturnHubType.SHIPMONK]: "ShipMonk",
  [ExternalReturnHubType.RYDER]: "Ryder",
  [ExternalReturnHubType.DEPOSCO]: "Deposco",
  [ExternalReturnHubType.NETSUITE]: "NetSuite",
  [ExternalReturnHubType.MINTSOFT]: "Mintsoft",
};

function summarizeAddress(config: ReturnsCenterConfigFormValue): string {
  const parts = [config.shipToCity, config.shipToState, config.shipToCountry]
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "";
}

function formatSchedule(days: string): string {
  const trimmed = days.trim();
  if (!trimmed) {
    return "Not scheduled";
  }
  const n = parseInt(trimmed, 10);
  if (isNaN(n)) {
    return trimmed;
  }
  if (n === 1) {
    return "Every day";
  }
  return `Every ${n} days`;
}

function buildDetailLine(row: ReturnsCenterRowData): string {
  const parts: string[] = [];
  if (row.ownerTeamName) {
    parts.push(row.ownerTeamName);
  }
  if (row.schedule !== "Not scheduled") {
    parts.push(row.schedule);
  }
  if (row.addressSummary) {
    parts.push(row.addressSummary);
  }
  const providerName = row.provider
    ? (HUB_DISPLAY_NAMES[row.provider as ExternalReturnHubType] ?? row.provider)
    : "";
  if (providerName) {
    parts.push(providerName);
  }
  return parts.join(" · ");
}

const EMPTY_CONFIG: Omit<ReturnsCenterConfigFormValue, "returnsCenterId"> = {
  consolidationScheduleDays: "",
  shipToName: "",
  shipToStreet1: "",
  shipToStreet2: "",
  shipToCity: "",
  shipToState: "",
  shipToZip: "",
  shipToCountry: "",
  refulfillment: false,
  processingFee: "",
};

function centerLabel(center: {
  name: string;
  city: string | null;
  state: string | null;
}): string {
  return `${center.name}${center.city ? ` - ${center.city}` : ""}${center.state ? `, ${center.state}` : ""}`;
}

const ROW_BORDER: React.CSSProperties = {
  borderBottom: "1px solid var(--redo-colors-border-border-secondary)",
};

const ROW_MIN_HEIGHT: React.CSSProperties = { minHeight: 56 };

const ReturnsCenterRow = memo(function ReturnsCenterRow({
  row,
  onEdit,
  onRemove,
  onToggleRefulfillment,
  showRefulfillmentToggle,
  showEditButton,
  disabled,
  isLast,
}: {
  row: ReturnsCenterRowData;
  onEdit?: (centerId: string) => void;
  onRemove?: (centerId: string) => void;
  onToggleRefulfillment?: (centerId: string, value: boolean) => void;
  showRefulfillmentToggle?: boolean;
  showEditButton?: boolean;
  disabled: boolean;
  isLast: boolean;
}) {
  const detailLine = buildDetailLine(row);

  const handleToggle = useCallback(
    (value: boolean) => {
      onToggleRefulfillment?.(row.id, value);
    },
    [onToggleRefulfillment, row.id],
  );

  return (
    <Flex
      align="center"
      cursor={onEdit ? "pointer" : undefined}
      dir="row"
      gap="lg"
      hoverColor={onEdit ? "secondary" : undefined}
      justify="space-between"
      onClick={onEdit ? () => onEdit(row.id) : undefined}
      p="lg"
      style={isLast ? ROW_MIN_HEIGHT : { ...ROW_BORDER, ...ROW_MIN_HEIGHT }}
    >
      <Flex dir="column" gap="xxs" grow="1">
        <Text fontSize="xs" fontWeight="medium">
          {row.label}
        </Text>
        {detailLine && (
          <Text fontSize="xxs" textColor="tertiary">
            {detailLine}
          </Text>
        )}
      </Flex>
      {showRefulfillmentToggle && (
        <Flex onClick={(e: React.MouseEvent) => e.stopPropagation()} shrink={0}>
          <RedoToggle
            disabled={disabled}
            setValue={handleToggle}
            size="sm"
            value={row.refulfillment}
          />
        </Flex>
      )}
      {showEditButton && onEdit && (
        <Flex
          dir="row"
          gap="xs"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          shrink={0}
        >
          <RedoButton
            disabled={disabled}
            hierarchy="tertiary"
            IconLeading={Edit01Icon}
            onClick={() => onEdit(row.id)}
            size="xs"
            text="Edit"
          />
        </Flex>
      )}
      {onRemove && (
        <Flex
          dir="row"
          gap="xs"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          shrink={0}
        >
          <RedoButton
            disabled={disabled}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={() => onRemove(row.id)}
            size="xs"
          />
        </Flex>
      )}
    </Flex>
  );
});

export const AvailableReturnsCentersTable = memo(
  function AvailableReturnsCentersTable({
    input,
    disabled,
    hubMappings,
    headerRight,
    showRefulfillmentToggle,
    showEditButton,
    disableRowEdit,
    readOnly,
    teamId,
  }: {
    input: AvailableReturnsCentersForm;
    disabled: boolean;
    hubMappings?: Record<string, ExternalLocationHubFormConfig>;
    headerRight?: React.ReactNode;
    showRefulfillmentToggle?: boolean;
    /**
     * Show an explicit Edit button on each row and make rows clickable even
     * when `readOnly` (which only suppresses add/remove).
     */
    showEditButton?: boolean;
    /** Suppress row click editing while keeping add/remove available. */
    disableRowEdit?: boolean;
    readOnly?: boolean;
    /**
     * Team being configured. Required for editing a center's consolidation
     * ship-to address, which is chosen from the team's saved locations.
     */
    teamId?: string;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

    const { value: returnsCentersData, pending: returnsCentersPending } =
      useRpcData(rpcClient, "getReturnsCenters", { input: { sort: null } });

    const allReturnsCenters = useMemo(
      () => returnsCentersData?.returnsCenters ?? [],
      [returnsCentersData],
    );

    const configs = input.value;

    const [editingCenterId, setEditingCenterId] = useState<string | null>(null);
    const [editDraft, setEditDraft] =
      useState<ReturnsCenterConfigFormValue | null>(null);

    const openEditModal = useCallback(
      (centerId: string) => {
        const existing = configs.find((c) => c.returnsCenterId === centerId);
        setEditDraft(
          existing ?? { returnsCenterId: centerId, ...EMPTY_CONFIG },
        );
        setEditingCenterId(centerId);
      },
      [configs],
    );

    const closeEditModal = useCallback(() => {
      setEditingCenterId(null);
      setEditDraft(null);
    }, []);

    const saveEditModal = useCallback(() => {
      if (!editDraft) {
        return;
      }
      input.setValue(
        configs.map((c) =>
          c.returnsCenterId === editDraft.returnsCenterId ? editDraft : c,
        ),
      );
      closeEditModal();
    }, [editDraft, configs, input, closeEditModal]);

    const removeCenter = useCallback(
      (centerId: string) => {
        input.setValue(configs.filter((c) => c.returnsCenterId !== centerId));
      },
      [configs, input],
    );

    const addCenter = useCallback(
      (centerId: string) => {
        input.setValue([
          ...configs,
          { returnsCenterId: centerId, ...EMPTY_CONFIG },
        ]);
      },
      [configs, input],
    );

    const selectedCenterIds = useMemo(
      () => new Set(configs.map((c) => c.returnsCenterId)),
      [configs],
    );

    const toggleRefulfillment = useCallback(
      (centerId: string, value: boolean) => {
        input.setValue(
          configs.map((c) =>
            c.returnsCenterId === centerId ? { ...c, refulfillment: value } : c,
          ),
        );
      },
      [configs, input],
    );

    const rowsOfData: ReturnsCenterRowData[] = useMemo(() => {
      return configs.map((config) => {
        const center = allReturnsCenters.find(
          (c) => c.id === config.returnsCenterId,
        );
        const mapping = hubMappings?.[config.returnsCenterId];
        return {
          id: config.returnsCenterId,
          label: center ? centerLabel(center) : config.returnsCenterId,
          schedule: formatSchedule(config.consolidationScheduleDays),
          addressSummary: summarizeAddress(config),
          provider: mapping?.provider ?? "",
          locationId: mapping?.locationId ?? "",
          ownerTeamName: center?.ownerTeamName ?? "",
          refulfillment: config.refulfillment,
        };
      });
    }, [allReturnsCenters, configs, hubMappings]);

    const addOptions: RedoListItem<string>[] = useMemo(() => {
      return allReturnsCenters
        .filter((c) => !selectedCenterIds.has(c.id))
        .map((center) => ({
          type: "text" as const,
          id: center.id,
          value: center.id,
          text: centerLabel(center),
        }));
    }, [allReturnsCenters, selectedCenterIds]);

    const handleAddSelected = useCallback(
      ({ item }: { item: RedoListItem<string> }) => {
        addCenter(item.value);
      },
      [addCenter],
    );

    if (returnsCentersPending) {
      return (
        <Text fontSize="sm" textColor="tertiary">
          Loading returns centers...
        </Text>
      );
    }

    const editingCenter = editingCenterId
      ? allReturnsCenters.find((c) => c.id === editingCenterId)
      : null;

    const hasAvailableCenters = addOptions.length > 0;

    return (
      <Flex dir="column" gap="none">
        {!readOnly && hasAvailableCenters && (
          <Flex p="lg">
            <RedoSingleSelectDropdownInput
              disabled={disabled}
              options={addOptions}
              optionSelected={handleAddSelected}
              placeholder="Add a returns center..."
              size="xs"
            />
          </Flex>
        )}
        {rowsOfData.length > 0 && (
          <Flex
            align="center"
            justify="space-between"
            pb="sm"
            pt="lg"
            px="lg"
            style={ROW_BORDER}
            w="full"
          >
            <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
              Returns Center
            </Text>
            {showRefulfillmentToggle && (
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                Re-fulfillment
              </Text>
            )}
            {headerRight}
          </Flex>
        )}
        <Flex align="stretch" dir="column" gap="none">
          {rowsOfData.map((row, index) => (
            <ReturnsCenterRow
              disabled={disabled}
              isLast={index === rowsOfData.length - 1}
              key={row.id}
              onEdit={
                (!readOnly || showEditButton) && !disableRowEdit
                  ? openEditModal
                  : undefined
              }
              onRemove={readOnly ? undefined : removeCenter}
              onToggleRefulfillment={
                showRefulfillmentToggle ? toggleRefulfillment : undefined
              }
              row={row}
              showEditButton={showEditButton}
              showRefulfillmentToggle={showRefulfillmentToggle}
            />
          ))}
        </Flex>

        {editDraft && editingCenter && (
          <EditReturnsCenterModal
            centerLabel={centerLabel(editingCenter)}
            draft={editDraft}
            onClose={closeEditModal}
            onSave={saveEditModal}
            setDraft={setEditDraft}
            teamId={teamId}
          />
        )}
      </Flex>
    );
  },
);

interface SelectableShipToAddress {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state?: string;
  zip: string;
  country: string;
}

/**
 * A consolidation ship-to address is only usable once it has the parts a
 * shipment needs, and a 2-letter ISO country (matching the merchant-side
 * editor and the ConsolidationShipToAddress contract). Anything short of that
 * is not offered as a selectable destination.
 */
function draftToShipToAddress(
  draft: ReturnsCenterConfigFormValue,
): SelectableShipToAddress | null {
  const country = draft.shipToCountry.trim().toUpperCase();
  if (
    !draft.shipToName.trim() ||
    !draft.shipToStreet1.trim() ||
    !draft.shipToCity.trim() ||
    !draft.shipToZip.trim() ||
    country.length !== 2
  ) {
    return null;
  }
  return {
    name: draft.shipToName.trim(),
    street1: draft.shipToStreet1.trim(),
    street2: draft.shipToStreet2.trim() || undefined,
    city: draft.shipToCity.trim(),
    state: draft.shipToState.trim() || undefined,
    zip: draft.shipToZip.trim(),
    country,
  };
}

function teamLocationToShipToAddress(
  location: TeamLocation,
): SelectableShipToAddress | null {
  const country = location.country?.trim().toUpperCase() ?? "";
  if (
    !location.name?.trim() ||
    !location.street1?.trim() ||
    !location.city?.trim() ||
    !location.postalCode?.trim() ||
    country.length !== 2
  ) {
    return null;
  }
  return {
    name: location.name.trim(),
    street1: location.street1.trim(),
    street2: location.street2?.trim() || undefined,
    city: location.city.trim(),
    state: location.state?.trim() || undefined,
    zip: location.postalCode.trim(),
    country,
  };
}

function shipToAddressesMatch(
  a: SelectableShipToAddress,
  b: SelectableShipToAddress,
): boolean {
  return (
    a.name === b.name &&
    a.street1 === b.street1 &&
    (a.street2 ?? "") === (b.street2 ?? "") &&
    a.city === b.city &&
    (a.state ?? "") === (b.state ?? "") &&
    a.zip === b.zip &&
    a.country === b.country
  );
}

function formatShipToAddressLine(address: SelectableShipToAddress): string {
  const locality = [
    address.city,
    [address.state, address.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [address.street1, address.street2, locality, address.country]
    .filter(Boolean)
    .join(" · ");
}

function makeShipToLocationItem(
  id: string,
  address: SelectableShipToAddress,
  fallbackName = "Saved location",
): RedoListItem<SelectableShipToAddress> {
  return {
    id,
    supportingText: formatShipToAddressLine(address),
    text: address.name || fallbackName,
    type: "text",
    value: address,
  };
}

const EditReturnsCenterModal = memo(function EditReturnsCenterModal({
  centerLabel,
  draft,
  setDraft,
  onClose,
  onSave,
  teamId,
}: {
  centerLabel: string;
  draft: ReturnsCenterConfigFormValue;
  setDraft: (value: ReturnsCenterConfigFormValue) => void;
  onClose: () => void;
  onSave: () => void;
  teamId?: string;
}) {
  const updateField = useCallback(
    (field: keyof ReturnsCenterConfigFormValue, value: string) => {
      setDraft({ ...draft, [field]: value });
    },
    [draft, setDraft],
  );

  const locationsQuery = useRpcQuery(
    RedoAdminRpcClientContext,
    "getTeamLocationsForAdmin",
    { teamId: teamId ?? "" },
    { enabled: !!teamId },
  );

  const locationItems = useMemo<RedoListItem<SelectableShipToAddress>[]>(
    () =>
      (locationsQuery.data?.locations ?? []).flatMap((location) => {
        const address = teamLocationToShipToAddress(location);
        return address ? [makeShipToLocationItem(location.id, address)] : [];
      }),
    [locationsQuery.data],
  );

  const selectedLocation = useMemo(() => {
    const current = draftToShipToAddress(draft);
    if (!current) {
      return undefined;
    }
    const match = locationItems.find((item) =>
      shipToAddressesMatch(current, item.value),
    );
    return (
      match ??
      makeShipToLocationItem(
        "current-ship-to-address",
        current,
        "Current destination",
      )
    );
  }, [draft, locationItems]);

  const applyAddress = useCallback(
    (address: SelectableShipToAddress) => {
      setDraft({
        ...draft,
        shipToName: address.name,
        shipToStreet1: address.street1,
        shipToStreet2: address.street2 ?? "",
        shipToCity: address.city,
        shipToState: address.state ?? "",
        shipToZip: address.zip,
        shipToCountry: address.country,
      });
    },
    [draft, setDraft],
  );

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Save",
        onClick: onSave,
        disabled: false,
        size: "xs",
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: onClose,
        disabled: false,
        size: "xs",
      }}
      subtitle="Configure billing and consolidation for this returns center."
      title={centerLabel}
    >
      <Flex dir="column" gap="lg">
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            Billing
          </Text>
          <RedoTextInput
            IconTrailing={() => <span>USD</span>}
            inputTypeHint="number"
            label="Processing fee override"
            min={0}
            placeholder="0.00"
            setValue={(v) => updateField("processingFee", v)}
            step={0.01}
            value={draft.processingFee}
          />
          <Text fontSize="xs" textColor="tertiary">
            If empty, uses the default processing fee for this merchant.
          </Text>
        </Flex>
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            Returns Consolidation
          </Text>
          <RedoTextInput
            inputTypeHint="number"
            label="Days between shipments"
            min={1}
            placeholder="e.g. 7"
            setValue={(v) => updateField("consolidationScheduleDays", v)}
            step={1}
            value={draft.consolidationScheduleDays}
          />
          <Text fontSize="xs" textColor="tertiary">
            Controls how often consolidated returns are shipped for this center.
          </Text>
        </Flex>
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            Consolidation ship-to address
          </Text>
          <RedoSingleSelectDropdownInput<SelectableShipToAddress>
            description="The saved location that receives consolidated returns shipped from this center."
            emptyListMessage="No saved locations for this team"
            fullWidth
            itemsLoading={locationsQuery.isLoading}
            options={locationItems}
            optionSelected={({ item }) => applyAddress(item.value)}
            placeholder="Select a saved location"
            selectedItem={selectedLocation}
            size="xs"
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
